/**
 * CMOE dual-write / shadow validation performance counters (measurement only).
 */

/** @typedef {{ operation: string, durationMs: number, reads: number, writes: number, atMs: number }} CmoePerfSample */

const samples = /** @type {CmoePerfSample[]} */ ([]);
let totalReads = 0;
let totalWrites = 0;

const MAX_SAMPLES = 200;

/**
 * @param {{ reads?: number, writes?: number }} [io]
 */
export function recordCmoeIo(io = {}) {
  totalReads += io.reads || 0;
  totalWrites += io.writes || 0;
}

/**
 * @param {string} operation
 * @param {number} durationMs
 * @param {{ reads?: number, writes?: number }} [io]
 */
export function recordCmoeTiming(operation, durationMs, io = {}) {
  recordCmoeIo(io);
  samples.push({
    operation,
    durationMs,
    reads: io.reads || 0,
    writes: io.writes || 0,
    atMs: Date.now(),
  });
  if (samples.length > MAX_SAMPLES) samples.shift();
}

export function resetCmoePerformanceBaseline() {
  samples.length = 0;
  totalReads = 0;
  totalWrites = 0;
}

/**
 * @param {() => Promise<T>} fn
 * @param {string} operation
 * @param {{ reads?: number, writes?: number }} [io]
 * @returns {Promise<T>}
 * @template T
 */
export async function withCmoeTiming(operation, fn, io = {}) {
  const start = typeof performance !== "undefined" ? performance.now() : Date.now();
  try {
    return await fn();
  } finally {
    const end = typeof performance !== "undefined" ? performance.now() : Date.now();
    recordCmoeTiming(operation, end - start, io);
  }
}

/**
 * @returns {import('./cmoePerformanceBaseline').CmoePerfReport}
 */
export function buildCmoePerformanceReport() {
  const byOp = {};
  for (const s of samples) {
    if (!byOp[s.operation]) {
      byOp[s.operation] = { count: 0, totalMs: 0, maxMs: 0, reads: 0, writes: 0 };
    }
    const row = byOp[s.operation];
    row.count += 1;
    row.totalMs += s.durationMs;
    row.maxMs = Math.max(row.maxMs, s.durationMs);
    row.reads += s.reads;
    row.writes += s.writes;
  }
  const operations = Object.entries(byOp).map(([operation, row]) => ({
    operation,
    count: row.count,
    avgMs: row.count ? row.totalMs / row.count : 0,
    maxMs: row.maxMs,
    reads: row.reads,
    writes: row.writes,
  }));
  return {
    generatedAtMs: Date.now(),
    sampleCount: samples.length,
    totalReads,
    totalWrites,
    operations,
  };
}

/**
 * @typedef {Object} CmoePerfReport
 * @property {number} generatedAtMs
 * @property {number} sampleCount
 * @property {number} totalReads
 * @property {number} totalWrites
 * @property {Array<{ operation: string, count: number, avgMs: number, maxMs: number, reads: number, writes: number }>} operations
 */

export function printCmoePerformanceReport() {
  const report = buildCmoePerformanceReport();
  console.info("[cmoe-perf]", JSON.stringify(report, null, 2));
  return report;
}
